import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Input from '../../components/ui/Input';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import { useAuth } from '../../contexts/AuthContext';

const Login = () => {
  const navigate = useNavigate();
  const { signIn } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setError('');
    setLoading(true);

    try {
      const { data, error: signInError } = await signIn(email, password);

      if (signInError) {
        setError(signInError?.message || 'Invalid email or password');
        setLoading(false);
        return;
      }

      navigate('/account-dashboard');
    } catch (err) {
      setError(err?.message || 'An unexpected error occurred. Please try again.');
      setLoading(false);
    }
  };

  const handleDemoLogin = (demoEmail, demoPassword) => {
    setEmail(demoEmail);
    setPassword(demoPassword);
  };

  return (
    <div className="min-h-screen bg-[var(--color-background)]">
      <Header />
      <main className="pt-20 pb-12 md:pb-16 lg:pb-20">
        <div className="max-w-md mx-auto px-4 md:px-6 lg:px-8">
          <div className="text-center mb-8 md:mb-10">
            <div className="inline-flex items-center justify-center w-16 h-16 md:w-20 md:h-20 rounded-2xl bg-gradient-to-br from-[var(--color-primary)] to-[var(--color-secondary)] mb-4 md:mb-6">
              <Icon name="Lock" size={32} color="#FFFFFF" />
            </div>
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-[var(--color-foreground)] mb-3 md:mb-4">
              Welcome Back
            </h1>
            <p className="text-base md:text-lg text-[var(--color-muted-foreground)]">
              Sign in to your Kryptostash account
            </p>
          </div>

          <div className="bg-[var(--color-card)] rounded-2xl shadow-lg border border-[var(--color-border)] p-6 md:p-8 mb-6">
            <form onSubmit={handleSubmit} className="space-y-5">
              {error && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                  <div className="flex items-center gap-2">
                    <Icon name="AlertCircle" size={20} color="#dc2626" />
                    <p className="text-sm text-red-600">{error}</p>
                  </div>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-[var(--color-foreground)] mb-2">
                  Email Address
                </label>
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e?.target?.value)}
                  placeholder="Enter your email"
                  required
                  disabled={loading}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-[var(--color-foreground)] mb-2">
                  Password
                </label>
                <div className="relative">
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e?.target?.value)}
                    placeholder="Enter your password"
                    required
                    disabled={loading}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--color-muted-foreground)] hover:text-[var(--color-foreground)]"
                  >
                    <Icon name={showPassword ? 'EyeOff' : 'Eye'} size={20} />
                  </button>
                </div>
              </div>

              <Button
                type="submit"
                variant="default"
                size="lg"
                fullWidth
                disabled={loading}
                iconName={loading ? undefined : 'ArrowRight'}
                iconPosition="right"
              >
                {loading ? 'Signing in...' : 'Sign In'}
              </Button>
            </form>

            <div className="mt-6 pt-6 border-t border-[var(--color-border)]">
              <p className="text-sm text-[var(--color-muted-foreground)] text-center mb-4">
                Demo Credentials for Testing:
              </p>
              <div className="space-y-2">
                <button
                  type="button"
                  onClick={() => handleDemoLogin('admin@kryptostash.com', 'admin123')}
                  className="w-full p-3 bg-[var(--color-muted)] hover:bg-[var(--color-muted)]/80 rounded-lg text-left transition-colors"
                >
                  <p className="text-sm font-medium text-[var(--color-foreground)]">Admin Account</p>
                  <p className="text-xs text-[var(--color-muted-foreground)] mt-1">
                    admin@kryptostash.com / admin123
                  </p>
                </button>
                <button
                  type="button"
                  onClick={() => handleDemoLogin('user@kryptostash.com', 'user123')}
                  className="w-full p-3 bg-[var(--color-muted)] hover:bg-[var(--color-muted)]/80 rounded-lg text-left transition-colors"
                >
                  <p className="text-sm font-medium text-[var(--color-foreground)]">User Account</p>
                  <p className="text-xs text-[var(--color-muted-foreground)] mt-1">
                    user@kryptostash.com / user123
                  </p>
                </button>
              </div>
            </div>
          </div>

          <div className="text-center">
            <p className="text-sm text-[var(--color-muted-foreground)]">
              Don't have an account?{' '}
              <Link to="/registration" className="text-[var(--color-primary)] hover:underline font-medium">
                Sign up here
              </Link>
            </p>
          </div>
        </div>
      </main>
      <footer className="border-t border-[var(--color-border)] bg-[var(--color-card)] py-6 md:py-8">
        <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-xs md:text-sm text-[var(--color-muted-foreground)]">
              © {new Date()?.getFullYear()} Kryptostash. All rights reserved.
            </p>
            <div className="flex items-center gap-4 md:gap-6">
              <a href="#" className="text-xs md:text-sm text-[var(--color-muted-foreground)] hover:text-[var(--color-foreground)]">
                Privacy Policy
              </a>
              <a href="#" className="text-xs md:text-sm text-[var(--color-muted-foreground)] hover:text-[var(--color-foreground)]">
                Terms of Service
              </a>
              <a href="#" className="text-xs md:text-sm text-[var(--color-muted-foreground)] hover:text-[var(--color-foreground)]">
                Support
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Login;